// @ts-check

/** @typedef {"netbanking" | "upi" | "debit" | "credit" | "wallets" | "cheque"} PaymentGatewayTypes */

/**
 * @typedef {Object} SupportConfiguration
 * @property {string} supportEmail
 * @property {string} billingEmail
 * @property {string[]} contactNumbers
 * @property {string} duration
 * @property {string} brandName
 * @property {string?} contactUs
 */

/**
 * @typedef {Object} SalesConfiguration
 * @property {string} phoneNumber
 * @property {number} hoursStart
 * @property {number} hoursEnd
 */

/**
 * @typedef {Object} StoreFrontUrls
 * @property {string} offlinePaymentOptions
 * @property {string} supersiteUrl
 * @property {string} panelInterface
 * @property {string} adamUrl
 * @property {string} whoisUrl
 * @property {string} sampleEmail
 */

/**
 * @typedef {"privacyPolicy" | "legal"} ComplianceUrls
 */

/**
 * @typedef {Object} BrandUrls
 * @property {StoreFrontUrls} storefront
 * @property {Record<ComplianceUrls, string> & {ccpa?: string} } compliance
 */

/**
 * @typedef {Object} PaymentSettings
 * @property {"modern" | "list"} displayMode
 * @property {PaymentGatewayTypes[]} autoRenewDisallowedFor
 * @property {Record<PaymentGatewayTypes, number>} [categories] PG Mapping
 */

/**
 * @typedef {Object} Scenes
 * @property {boolean} AllowMultiRoleLogin
 * @property {boolean} [ARTokenNotification]
 * @property {boolean} [CrossSellBanners]
 * @property {boolean} AddFunds
 * @property {boolean} Walkthrough
 * @property {boolean} ShowTitanAgreement
 * @property {boolean} ShowOrderTransactions
 * @property {boolean} DisableRenewForOnHoldOrders
 * @property {boolean} AllowSystemInvoiceActions
 * @property {boolean} ShowCustomerIconOnOrderCard
 * @property {boolean} ApplyBuyMoreCustomerStyles
 * @property {boolean} CancelPendingInvoices
 * @property {boolean} GetCustomerShortcuts
 * @property {boolean} ResellerLockOrderForCustomer
 * @property {boolean} UseOBLegalAgreements
 * @property {boolean} ShowCustomerOnHoldOrders
 * @property {boolean} ShowOrderSuspensionAlerts
 * @property {boolean} ShowInvoiceTabs
 * @property {boolean} AllowPaymentForUser
 * @property {boolean} ShowDomainTransfer
 * @property {boolean} EmbedHelpdesk
 * @property {boolean} ManagePendingExecution
 * @property {boolean} AllowDesignatedAgent
 * @property {boolean} ShowCancelPendingInvoiceModal
 * @property {boolean} AllowMoveDomain
 */

/** @typedef {"resellerclub" | "bigrock"} Brand */

/**
 * @typedef {Object} BrandConfiguration
 * @property {Brand} brand
 * @property {Role} preferredUserRole
 * @property {Omit<import("../../webpro").SiteDetails, "url">} siteDetails
 * @property {SupportConfiguration?} support Support configuration for this brand.
 * @property {SalesConfiguration?} sales Sales configuration for this brand.
 * @property {BrandUrls} urls Brand Specific Urls
 * @property {Scenes} scenes
 * @property {PaymentSettings} paymentSettings PG Mapping
 * @property {{ productName?: string, headerIconUrl?: string|null, showSources?: boolean, brandName?: string, sourceApp?: number, promptId?: string, model?: string, caseCreationEnabled?: boolean, customCsatEnabled?: boolean }=} atlasConfig Optional Atlas AI chat configuration. Controls chat header branding, sources visibility, feedback API settings, and case-creation UI. Defaults from BrandConfigurationOperations.atlasConfig.
 */

/** @type {Record<string, BrandConfiguration>} */
const BrandConfigurationById = {
  247133: {
    brand: "bigrock",
    preferredUserRole: "customer",
    urls: {
      compliance: {
        legal: "https://www.bigrock.in/support/legal.php",
        privacyPolicy: "https://newfold.com/privacy-center"
      },
      storefront: {
        offlinePaymentOptions: "https://bigrock.in/offline-payment-options",
        supersiteUrl: "https://www.bigrock.in",
        adamUrl: "https://helpdesk.bigrock.in",
        panelInterface: "https://myorders.bigrock.in",
        whoisUrl: "https://bigrock.in/whois-lookup.php",
        sampleEmail: "yourdomain@bigrock.in"
      }
    },
    paymentSettings: {
      displayMode: "modern",
      autoRenewDisallowedFor: ["netbanking"],
      categories: {
        netbanking: 111891,
        upi: 59483,
        debit: 118298,
        credit: 225968,
        wallets: 118299,
        cheque: -1
      }
    },
    scenes: {
      AllowMultiRoleLogin: false,
      ARTokenNotification: true,
      CrossSellBanners: true,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: true,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,
      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: true,
      ManagePendingExecution: true,
      AllowDesignatedAgent: false,
      ShowCancelPendingInvoiceModal: true,
      AllowMoveDomain: true
    },
    siteDetails: {
      name: "BigRock.in",
      resellerid: "247133",
      brandingurl: "manage.bigrock.in",
      company: "Endurance International Group (India) Private Limited",
      brandname: "BigRock",
      sellingcurrencysymbol: "INR",
      accountingcurrencysymbol: "INR",
      abuseemailaddr: "abuse@bigrock.com",
      salesemailaddr: "sales@bigrock.com",
      billingemailaddr: "billing@bigrock.com",
      billingtelnocc: "91",
      billingtelno: "8242868080",
      salestelnocc: "91",
      salestelno: "8242868080",
      bu_details: { bu_name: "BigRock", bu_id: "247133" },
      supportsautorenew: true
    },
    support: {
      brandName: "BigRock",
      supportEmail: "support@bigrock.com",
      billingEmail: "billing@bigrock.com",
      contactNumbers: ["0824-6614011", "0824-2868080"],
      duration: "(9AM - 8PM)",
      contactUs: "https://www.bigrock.in/support/contact-us"
    },
    sales: {
      phoneNumber: "1-800-266-7625",
      hoursStart: 10,
      hoursEnd: 19
    }
  },
  20794: {
    brand: "bigrock",
    preferredUserRole: "customer",
    urls: {
      compliance: {
        legal: "https://www.bigrock.in/support/legal.php",
        privacyPolicy: "https://newfold.com/privacy-center"
      },
      storefront: {
        offlinePaymentOptions: "https://www.bigrock.in/offline-payment-options",
        adamUrl: "",
        panelInterface: null,
        supersiteUrl: "https://staging-qa.bigrock.in",
        whoisUrl: "https://bigrock.in/whois-lookup.php",
        sampleEmail: "yourdomain@bigrock.in"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: {
        netbanking: 650,
        upi: 300421,
        debit: 650,
        credit: 844,
        wallets: 650,
        cheque: -1
      }
    },
    scenes: {
      AllowMultiRoleLogin: false,
      ARTokenNotification: false,
      CrossSellBanners: false,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: true,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,

      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      AllowDesignatedAgent: false,
      ShowCancelPendingInvoiceModal: true,
      AllowMoveDomain: true
    },
    siteDetails: null,
    support: {
      brandName: "BigRock",
      supportEmail: "support@bigrock.com",
      billingEmail: "billing@bigrock.com",
      contactNumbers: ["0824-6614011", "0824-2868080"],
      duration: "(24/7)",
      contactUs: "https://www.bigrock.in/support/contact-us"
    },
    sales: {
      phoneNumber: "1-800-266-7625",
      hoursStart: 10,
      hoursEnd: 19
    }
  },
  330623: {
    brand: "resellerclub",
    preferredUserRole: "reseller",
    urls: {
      compliance: {
        legal: "https://india.resellerclub.com/legal-agreements",
        privacyPolicy: "https://newfold.com/privacy-center"
      },
      storefront: {
        offlinePaymentOptions: "",
        adamUrl: "https://helpdesk.resellerclub.com",
        panelInterface: "https://rclubindia.webpropanel.com",
        supersiteUrl: null,
        whoisUrl: "https://rclubindia.myorderbox.com/whois.jsp",
        sampleEmail: "yourdomain@resellerclub.in"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: null
    },
    scenes: {
      AllowMultiRoleLogin: false,
      ARTokenNotification: false,
      CrossSellBanners: true,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: false,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,

      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      AllowDesignatedAgent: true,
      ShowCancelPendingInvoiceModal: false,
      AllowMoveDomain: true
    },
    siteDetails: {
      name: "admin",
      resellerid: "330623",
      brandingurl: "manage.india.resellerclub.com",
      company: "Endurance International Group (India) Private Limited",
      brandname: "ResellerClub - India",
      sellingcurrencysymbol: "INR",
      accountingcurrencysymbol: "INR",
      abuseemailaddr: "abuse@publicdomainregistry.com",
      salesemailaddr: "sales@resellerclub.com",
      billingemailaddr: "billing@resellerclub.com",
      billingtelnocc: "91",
      billingtelno: "2267209090",
      salestelnocc: "91",
      salestelno: "2267209090",
      bu_details: { bu_name: "ResellerClub-India", bu_id: "330623" },
      supportsautorenew: true
    },
    support: {
      brandName: "ResellerClub - India",
      supportEmail: "",
      billingEmail: "",
      contactNumbers: [],
      duration: "",
      contactUs: ""
    },
    sales: {
      phoneNumber: "",
      hoursStart: null,
      hoursEnd: null
    }
  },
  331653: {
    brand: "resellerclub",
    preferredUserRole: "reseller",
    urls: {
      compliance: {
        legal: "https://resellerclub.com/legal-agreements",
        privacyPolicy: "https://newfold.com/privacy-center"
      },
      storefront: {
        offlinePaymentOptions: "",
        adamUrl: "https://helpdesk.resellerclub.com",
        panelInterface: "https://rclubbr.webpropanel.com",
        supersiteUrl: null,
        whoisUrl: "https://rclubbr.myorderbox.com/whois.jsp",
        sampleEmail: "yourdomain@resellerclub.com"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: null
    },
    scenes: {
      AllowMultiRoleLogin: false,
      ARTokenNotification: false,
      CrossSellBanners: true,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: false,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,

      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      ShowCancelPendingInvoiceModal: false,
      AllowDesignatedAgent: true,
      AllowMoveDomain: true
    },
    siteDetails: null,
    support: {
      brandName: "ResellerClub Brazil",
      supportEmail: "",
      billingEmail: "",
      contactNumbers: [],
      duration: "",
      contactUs: ""
    },
    sales: {
      phoneNumber: "",
      hoursStart: null,
      hoursEnd: null
    }
  },
  355948: {
    brand: "resellerclub",
    preferredUserRole: "reseller",
    urls: {
      compliance: {
        legal: "https://resellerclub.com/legal-agreements",
        privacyPolicy: "https://newfold.com/privacy-center"
      },
      storefront: {
        offlinePaymentOptions: "",
        adamUrl: "https://helpdesk.resellerclub.com",
        panelInterface: "https://rclubtr.webpropanel.com",
        supersiteUrl: null,
        whoisUrl: "https://rclubtr.myorderbox.com/whois.jsp",
        sampleEmail: "yourdomain@resellerclub.com"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: null
    },
    scenes: {
      AllowMultiRoleLogin: false,
      ARTokenNotification: false,
      CrossSellBanners: true,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: false,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,

      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      ShowCancelPendingInvoiceModal: false,
      AllowDesignatedAgent: true,
      AllowMoveDomain: true
    },
    siteDetails: null,
    support: {
      brandName: "ResellerClub Turkey",
      supportEmail: "",
      billingEmail: "",
      contactNumbers: [],
      duration: "",
      contactUs: ""
    },
    sales: {
      phoneNumber: "",
      hoursStart: null,
      hoursEnd: null
    }
  },
  415316: {
    brand: "resellerclub",
    preferredUserRole: "reseller",
    urls: {
      compliance: {
        legal: "https://resellerclub.com/legal-agreements",
        privacyPolicy: "https://newfold.com/privacy-center"
      },
      storefront: {
        offlinePaymentOptions: "",
        adamUrl: "https://helpdesk.resellerclub.com",
        panelInterface: "https://rclubid.webpropanel.com",
        supersiteUrl: null,
        whoisUrl: "https://rclubid.myorderbox.com/whois.jsp",
        sampleEmail: "yourdomain@resellerclub.com"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: null
    },
    scenes: {
      AllowMultiRoleLogin: false,
      ARTokenNotification: false,
      CrossSellBanners: true,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: false,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,

      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      ShowCancelPendingInvoiceModal: false,
      AllowDesignatedAgent: true,
      AllowMoveDomain: true
    },
    siteDetails: null,
    support: {
      brandName: "ResellerClub Indonesia",
      supportEmail: "",
      billingEmail: "",
      contactNumbers: [],
      duration: "",
      contactUs: ""
    },
    sales: {
      phoneNumber: "",
      hoursStart: null,
      hoursEnd: null
    }
  },
  1: {
    brand: "resellerclub",
    preferredUserRole: "reseller",
    urls: {
      compliance: {
        legal: "https://resellerclub.com/legal-agreements",
        privacyPolicy: "https://newfold.com/privacy-center",
        ccpa: "https://www.newfold.com/privacy-center/addendum-for-california-users"
      },
      storefront: {
        offlinePaymentOptions: "",
        adamUrl: "https://helpdesk.resellerclub.com",
        panelInterface: "https://resellerclub.webpropanel.com",
        supersiteUrl: null,
        whoisUrl: "https://manage.resellerclub.com/whois.jsp",
        sampleEmail: "yourdomain@resellerclub.com"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: null
    },
    scenes: {
      AllowMultiRoleLogin: false,
      ARTokenNotification: false,
      CrossSellBanners: true,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: false,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,

      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      ShowCancelPendingInvoiceModal: false,
      AllowDesignatedAgent: true,
      AllowMoveDomain: true
    },
    siteDetails: {
      name: "ResellerClub",
      resellerid: "1",
      brandingurl: "manage.resellerclub.com",
      company: "P.D.R. Solutions (U.S.) LLC",
      brandname: "ResellerClub",
      sellingcurrencysymbol: "USD",
      accountingcurrencysymbol: "INR",
      abuseemailaddr: "abuse@publicdomainregistry.com",
      salesemailaddr: "sales@resellerclub.com",
      billingemailaddr: "billing@resellerclub.com",
      billingtelnocc: "1",
      billingtelno: "4152361970",
      salestelnocc: "1",
      salestelno: "4152361970",
      bu_details: { bu_name: "ResellerClub-Global", bu_id: "1" },
      supportsautorenew: true
    },
    support: {
      brandName: "ResellerClub",
      supportEmail: "",
      billingEmail: "",
      contactNumbers: [],
      duration: "",
      contactUs: ""
    },
    sales: {
      phoneNumber: "",
      hoursStart: null,
      hoursEnd: null
    }
  },
  999999998: {
    brand: "resellerclub",
    preferredUserRole: "reseller",
    urls: {
      compliance: {
        legal: "https://resellerclub.com/legal-agreements",
        privacyPolicy: "https://newfold.com/privacy-center"
      },
      storefront: {
        offlinePaymentOptions: "",
        adamUrl: "",
        panelInterface: "https://demo.webpropanel.com",
        supersiteUrl: null,
        whoisUrl: "https://demo.myorderbox.com/whois.jsp",
        sampleEmail: "yourdomain@resellerclub.com"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: null
    },
    scenes: {
      AllowMultiRoleLogin: true,
      ARTokenNotification: false,
      CrossSellBanners: true,
      AddFunds: true,
      ShowTitanAgreement: false,
      DisableRenewForOnHoldOrders: false,
      AllowSystemInvoiceActions: false,
      ShowCustomerIconOnOrderCard: false,

      ApplyBuyMoreCustomerStyles: false,
      CancelPendingInvoices: false,
      GetCustomerShortcuts: false,
      UseOBLegalAgreements: false,
      ResellerLockOrderForCustomer: false,

      ShowCustomerOnHoldOrders: false,
      ShowOrderSuspensionAlerts: false,
      ShowInvoiceTabs: false,
      AllowPaymentForUser: false,
      ShowDomainTransfer: true,
      Walkthrough: true,
      ShowOrderTransactions: true,
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      ShowCancelPendingInvoiceModal: false,
      AllowDesignatedAgent: true,
      AllowMoveDomain: true
    },
    siteDetails: null,
    support: {
      brandName: "Demo Registrar",
      supportEmail: "",
      billingEmail: "",
      contactNumbers: [],
      duration: "",
      contactUs: ""
    },
    sales: {
      phoneNumber: "",
      hoursStart: null,
      hoursEnd: null
    }
  }
};

/**
 *
 * @param {Role} role
 * @returns {BrandConfiguration}
 */
function createDefaultBrandConfigurationFor(role) {
  return {
    brand: "resellerclub",
    preferredUserRole: "customer",
    urls: {
      compliance: {
        legal: "/legal",
        privacyPolicy: "/legal"
      },
      storefront: {
        offlinePaymentOptions: "",
        adamUrl: "",
        panelInterface: window.location.origin,
        supersiteUrl: null,
        whoisUrl: "https://publicdomainregistry.com/whois",
        sampleEmail: "yourdomain@domain.com"
      }
    },
    paymentSettings: {
      displayMode: "list",
      autoRenewDisallowedFor: [],
      categories: null
    },
    scenes: {
      AllowMultiRoleLogin: true,
      ARTokenNotification: false,
      CrossSellBanners: false,
      AddFunds: true, // enabled for all users (resellers and customers)
      ShowTitanAgreement: role === "customer",
      DisableRenewForOnHoldOrders: role === "customer",
      AllowSystemInvoiceActions: role === "customer",
      ShowCustomerIconOnOrderCard: role === "customer",
      ApplyBuyMoreCustomerStyles: role === "customer",
      CancelPendingInvoices: role === "customer",
      GetCustomerShortcuts: role === "customer",
      UseOBLegalAgreements: role === "customer",
      ResellerLockOrderForCustomer: role === "customer",
      ShowCustomerOnHoldOrders: role === "customer",
      ShowOrderSuspensionAlerts: role === "customer",
      ShowInvoiceTabs: role !== "customer",
      AllowPaymentForUser: role !== "customer",
      ShowDomainTransfer: role !== "customer",
      Walkthrough: role === "reseller",
      ShowOrderTransactions: role === "reseller",
      EmbedHelpdesk: false,
      ManagePendingExecution: false,
      ShowCancelPendingInvoiceModal: false,
      AllowDesignatedAgent: true,
      AllowMoveDomain: role === "reseller"
    },
    siteDetails: null,
    support: {
      brandName: "",
      supportEmail: "",
      billingEmail: "",
      contactNumbers: [],
      duration: "",
      contactUs: ""
    },
    sales: {
      phoneNumber: "",
      hoursStart: null,
      hoursEnd: null
    }
  };
}

export const BrandConfigurationOperations = {
  /**
   *
   * @param {BrandConfiguration} brandConfiguration
   * @param {PaymentGatewayTypes} category
   * @returns {number}
   */
  mapCategoryToId: (brandConfiguration, category) =>
    brandConfiguration.paymentSettings.categories[category],
  /**
   *
   * @param {string} parentId
   * @param {Role?} role
   * @returns {BrandConfiguration?}
   */
  findById: (parentId, role = null) => {
    const configuration = BrandConfigurationById[parentId];
    if (configuration) {
      return configuration;
    }
    return role ? createDefaultBrandConfigurationFor(role) : null;
  },
  /**
   *
   * @param {import("react-redux").DefaultRootState} state
   * @returns {BrandConfiguration}
   */
  fromState: state =>
    BrandConfigurationOperations.findById(
      state.root.userInfo.parentid,
      state.root.userInfo.role
    ),
  /**
   * @param {string} url
   * @returns {BrandConfiguration?}
   */
  findByUrl: url =>
    Object.values(BrandConfigurationById).find(config =>
      config.urls.storefront.panelInterface?.includes(url)
    ),
  /**
   *
   * @param {Brand} brand
   * @returns {string}
   */
  findAnalyticsId: brand =>
    ["bigrock"].includes(brand) ? "GTM-TGPWBCQ8" : "GTM-5BRG5GJ",
  /**
   *
   * @param {Brand} brand
   * @returns {string | null}
   */
  findABTestingId: brand =>
    ["bigrock"].includes(brand) ? "0bdcf999-d87f-cb65-e394-05af52149f50" : null,
  /**
   * Atlas AI chat configuration including branding, sources visibility, and feedback API settings.
   * Reseller Club defaults to Fixie branding with sources hidden; other brands default to RockTalk with sources shown.
   * @param {BrandConfiguration|null|undefined} configuration
   * @returns {{ productName: string, headerIconUrl: string|null, showSources: boolean, brandName: string, sourceApp: number, promptId: string, model: string, caseCreationEnabled: boolean, customCsatEnabled: boolean }}
   */
  atlasConfig: configuration => {
    const sharedPromptId = "6ea32088-d7df-4933-a101-517960e12110";
    const sharedModel = "bigrock-gpt-4.1-mini";

    if (!configuration) {
      return {
        productName: "RockTalk",
        headerIconUrl: "https://myorders.bigrock.in/favicon.ico",
        showSources: true,
        brandName: "resellerclub",
        sourceApp: 13,
        promptId: sharedPromptId,
        model: sharedModel,
        caseCreationEnabled: false,
        customCsatEnabled: false
      };
    }

    /**
     * Brand-specific Atlas configuration defaults.
     * Add new brands here to extend support without modifying logic.
     * @type {Record<string, { productName: string, headerIconUrl: string, showSources: boolean, brandName: string, sourceApp: number, caseCreationEnabled?: boolean, customCsatEnabled?: boolean }>}
     */
    const ATLAS_BRAND_DEFAULTS = {
      resellerclub: {
        productName: "Fixie",
        headerIconUrl:
          "https://india.resellerclub.com/front/images/brand-common/favicon-416276.ico",
        showSources: false,
        brandName: "resellerclub",
        sourceApp: 13,
        caseCreationEnabled: true,
        customCsatEnabled: true
      },
      logicboxes: {
        productName: "Fixie",
        headerIconUrl: null,
        showSources: false,
        brandName: "logicboxes",
        sourceApp: 15,
        caseCreationEnabled: true,
        customCsatEnabled: true
      },
      supportnation: {
        productName: "Fixie",
        headerIconUrl: null,
        showSources: false,
        brandName: "supportnation",
        sourceApp: 14,
        caseCreationEnabled: true,
        customCsatEnabled: true
      },
      bigrock: {
        productName: "RockTalk",
        headerIconUrl: "https://myorders.bigrock.in/favicon.ico",
        showSources: true,
        brandName: "Bigrock",
        sourceApp: 7,
        caseCreationEnabled: true,
        customCsatEnabled: true
      }
    };

    // Fallback defaults for unknown brands (uses resellerclub branding + feedback defaults)
    const DEFAULT_BRAND_KEY = "resellerclub";
    const defaults =
      ATLAS_BRAND_DEFAULTS[configuration.brand] ||
      ATLAS_BRAND_DEFAULTS[DEFAULT_BRAND_KEY];

    const override = configuration.atlasConfig || {};
    return {
      productName: override.productName ?? defaults.productName,
      headerIconUrl:
        override.headerIconUrl !== undefined
          ? override.headerIconUrl
          : defaults.headerIconUrl,
      showSources: override.showSources ?? defaults.showSources,
      brandName: override.brandName ?? defaults.brandName,
      sourceApp: override.sourceApp ?? defaults.sourceApp,
      promptId: override.promptId ?? sharedPromptId,
      model: override.model ?? sharedModel,
      caseCreationEnabled:
        override.caseCreationEnabled ?? defaults.caseCreationEnabled ?? false,
      customCsatEnabled:
        override.customCsatEnabled ?? defaults.customCsatEnabled ?? false
    };
  }
};
